import type { Schedule } from "@/features/calendar/types";
import { useEffect, useMemo, useRef } from "react";

/** Each time slot is 12px (h-3), 4 slots per hour. */
const PIXELS_PER_HOUR = 48;

/** Where the grid starts when no schedule says otherwise. */
const DEFAULT_START_HOUR = 8;

/**
 * Open the grid on the working day rather than at midnight: find the earliest
 * `BYHOUR` across the schedules, back off an hour so the first slot is not
 * flush against the top, and scroll there.
 *
 * Shared by the day and week views, which used to carry the same two blocks
 * word for word. Returns the ref to hand to the `ScrollArea`.
 */
export function useWorkHoursScroll(schedules: Schedule[] = []) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const earliestHour = useMemo(() => {
    if (!schedules || schedules.length === 0) return DEFAULT_START_HOUR;

    let minHour = 24;
    schedules.forEach((schedule) => {
      try {
        const rules =
          schedule.rrules?.split("\n").filter((r) => r.trim()) || [];
        rules.forEach((rule) => {
          const match = rule.match(/BYHOUR=(\d{1,2})/);
          if (match) {
            const hour = parseInt(match[1]);
            if (hour < minHour) minHour = hour;
          }
        });
      } catch (error) {
        console.warn("Error parsing schedule rrules:", error);
      }
    });

    return minHour === 24 ? DEFAULT_START_HOUR : Math.max(0, minHour - 1); // Subtract 1 hour, min 0
  }, [schedules]);

  useEffect(() => {
    if (scrollAreaRef.current && earliestHour !== undefined) {
      const viewport = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]",
      );
      if (viewport) {
        viewport.scrollTop = earliestHour * PIXELS_PER_HOUR;
      }
    }
  }, [earliestHour, schedules]);

  return { scrollAreaRef, earliestHour };
}
